import React, { useState } from "react";
import usePageSeo from '../utils/usePageSeo';

export default function FAQPage() {
  usePageSeo({
    title: 'FAQ',
    description: 'Frequently asked questions about Poppyruz — reading books, uploading PDFs, themes, and Premium.',
    path: '/faq',
  });
  const faqs = [
    {
        q: "What is Poppyruz Premium?",
        a: "Premium unlocks unlimited book and theme uploads, removes ads, and gives you access to premium themes. It’s a one-time purchase for lifetime access.",
    },
    {
        q: "Can I upload my own PDFs?",
        a: "Yes. Upload a PDF on the Upload page and Poppyruz converts it into a readable, themed book with progress tracking.",
    },
    {
        q: "Are all kinds of PDF files supported?",
        a: "Yes, but we recommend uploading simple, plainly formatted PDF books for the best experience. We’re working on AI/ML-assisted automatic structuring of uploaded books, but for now you can manually edit any noisy text (click the page) if needed.",
    },
    {
        q: "What are themes?",
        a: "Themes let you set a background while reading. Some include audio. There are free and premium themes; with Premium, you can also upload your own on the Upload Themes page.",
    },
    {
        q: "Can I read books without Premium?",
        a: "Yes. You can read without Premium, but you can upload only up to four books and won’t be able to upload themes.",
    },
    {
        q: "Will new public-domain books be added continuously?",
        a: "Yes. We’ll keep adding new public-domain books regularly.",
    },
    {
        q: "Which payment methods are supported?",
        a: "Razorpay for India and PayPal for the US/international.",
    },
    {
        q: "How do I get help or report copyright issues?",
        a: "Use the “Report Copyright Violation” link in the footer or visit the Contact page. We usually reply within 1–2 business days.",
    }
  ];

  return (
    <main className="min-h-screen bg-white">
      <section className="max-w-3xl mx-auto px-6 py-16">
        <header className="text-center mb-10">
          <h1 className="text-4xl font-bold mb-3">Frequently Asked Questions</h1>
          <p className="text-gray-600">Quick answers about Premium and using Poppyruz.</p>
        </header>

        <div className="space-y-4">
          {faqs.map((item, idx) => (
            <FAQItem key={idx} q={item.q} a={item.a} defaultOpen={idx === 0} />
          ))}
        </div>

        <div className="mt-10 text-center text-sm text-gray-600">
          Still have questions? {" "}
          <a href="/contact" className="text-violet-900 hover:underline">Contact us</a> {" "}
        </div>
      </section>
    </main>
  );
}

function FAQItem({ q, a, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 p-4 text-left"
      >
        <span className="font-medium text-gray-900">{q}</span>
        <Chevron open={open} />
      </button>

      <div
        className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out px-4 ${
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden pb-4 text-gray-700">{a}</div>
      </div>
    </div>
  );
}

function Chevron({ open }) {
  return (
    <svg
      className={`h-5 w-5 transform transition-transform ${open ? "rotate-180" : "rotate-0"}`}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 011.06.02L10 10.17l3.71-2.94a.75.75 0 01.93 1.18l-4.24 3.36a.75.75 0 01-.93 0L5.21 8.27a.75.75 0 01.02-1.06z"
        clipRule="evenodd"
      />
    </svg>
  );
}
